import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/authContext";
import { isAdminUser } from "@/lib/admin";

export type RestyleVisibilityMode = "hidden" | "admin_only" | "public";
export type ShopOwnerFeaturesMode = "hidden" | "admin_only" | "public";

export interface StripePriceInfo {
  id: string;
  amount: number;
}
export type StripePricePlan = "insider_monthly" | "insider_yearly" | "shop_verification";
export type StripePriceMap = Partial<Record<StripePricePlan, StripePriceInfo>>;

export interface StripePricingSettings {
  defaults: StripePriceMap;
  overridesByCountry: Record<string, StripePriceMap>;
}

const fetchPublicFeatures = async () => {
  const res = await fetch("/api/features");
  if (!res.ok) {
    return {
      restyleVisibility: (localStorage.getItem("restyle_visibility_mode") as RestyleVisibilityMode) || "hidden",
      shopOwnerFeaturesMode: (localStorage.getItem("shop_owner_features_mode") as ShopOwnerFeaturesMode) || "hidden",
      stripePrices: { defaults: {}, overridesByCountry: {} }
    };
  }
  const json = await res.json().catch(() => null);

  const raw = String(json?.restyleVisibility || "hidden");
  const restyleVisibility: RestyleVisibilityMode =
    raw === "public" || raw === "admin_only" || raw === "hidden"
      ? (raw as RestyleVisibilityMode)
      : "hidden";

  const rawOwner = String(json?.shopOwnerFeaturesMode || "hidden");
  const shopOwnerFeaturesMode: ShopOwnerFeaturesMode =
    rawOwner === "public" || rawOwner === "admin_only" || rawOwner === "hidden"
      ? (rawOwner as ShopOwnerFeaturesMode)
      : "hidden";

  const stripePrices = json?.stripePrices;
  localStorage.setItem("restyle_visibility_mode", restyleVisibility);
  localStorage.setItem("shop_owner_features_mode", shopOwnerFeaturesMode);
  return { restyleVisibility, shopOwnerFeaturesMode, stripePrices };
};

export function useRestyleVisibility() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const { data } = useQuery({
    queryKey: ["feature-flags-public"],
    queryFn: fetchPublicFeatures,
    staleTime: 60_000,
    initialData: () => {
      const cached = localStorage.getItem("restyle_visibility_mode");
      if (cached) return { restyleVisibility: cached as RestyleVisibilityMode, shopOwnerFeaturesMode: (localStorage.getItem("shop_owner_features_mode") as ShopOwnerFeaturesMode) ?? "hidden" };
      return undefined;
    }
  });

  const mode = data?.restyleVisibility ?? (localStorage.getItem("restyle_visibility_mode") as RestyleVisibilityMode) ?? "hidden";
  const canSeeRestyle = useMemo(() => {
    if (mode === "public") return true;
    if (mode === "admin_only") return Boolean(isAdmin);
    return false;
  }, [mode, isAdmin]);

  return { mode, canSeeRestyle, isAdmin };
}

export function useShopOwnerFeatures() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const { data } = useQuery({
    queryKey: ["feature-flags-public"],
    queryFn: fetchPublicFeatures,
    staleTime: 30_000,
  });

  const mode = (data?.shopOwnerFeaturesMode ?? localStorage.getItem("shop_owner_features_mode") ?? "hidden") as ShopOwnerFeaturesMode;
  const canSeeShopOwner = useMemo(() => {
    if (mode === "public") return true;
    if (mode === "admin_only") return Boolean(isAdmin);
    return false;
  }, [mode, isAdmin]);

  return { shopOwnerMode: mode, canSeeShopOwner };
}


export function useStripePrices() {
  const { data } = useQuery({
    queryKey: ["feature-flags-public"],
    queryFn: fetchPublicFeatures,
    staleTime: 60_000,
  });

  const stripePrices = (data as any)?.stripePrices as StripePricingSettings | undefined;

  const getPrice = (plan: StripePricePlan, countryCode?: string | null): StripePriceInfo | null => {
    if (!stripePrices) return null;

    // 1. Check override
    if (countryCode) {
      const normalized = countryCode.trim().toUpperCase();
      const override = stripePrices.overridesByCountry?.[normalized]?.[plan];
      if (override) return override;
    }

    // 2. Check default
    return stripePrices.defaults?.[plan] || null;
  };

  return { stripePrices, getPrice };
}
